import {
  VuexModule,
  Module,
  Mutation,
  Action,
  getModule,
} from "vuex-module-decorators";
import store from "@/store";

import assetManagementPb from "@/proto/asset-mgmt/assetManagement_pb";
import * as api from "@/api/net-finder/assetManagement";
import { NetFinderCommonModule } from "@/store/modules/net-finder/common";
import { Pagination } from "@/api/net-finder/util";
import { ApiState, getErrorString, verifyAssetCSV } from "./util";
import { AssetView } from "@/libs/assetUtil";
import { AuthModule } from "../auth";

@Module({
  dynamic: true,
  store,
  name: "netFinder.AssetManagement",
  namespaced: true,
})
export class AssetManagement extends VuexModule {
  list: ApiState<assetManagementPb.AssetNodeListResponse.AsObject> = {
    data: null,
    loading: false,
    error: null,
  };
  pagination: Pagination = {
    offset: 0,
    limit: 20,
  };

  headersBasic: {
    text: string;
    value: string;
    sortable: boolean;
  }[] = [
    { text: "突合種別", value: "match", sortable: true },
    { text: "機器管理番号", value: "assetId", sortable: true },
    {
      text: "ホスト名（機器名）",
      value: "hostname",
      sortable: true,
    },
    { text: "機種種別・OS", value: "assetType", sortable: true },
    {
      text: "製品モデル名（型番）",
      value: "modelName",
      sortable: true,
    },
    {
      text: "製品シリアル番号",
      value: "serialNo",
      sortable: true,
    },
    {
      text: "製品ベンダー名（メーカー名）",
      value: "vendorName",
      sortable: true,
    },
    {
      text: "利用者名",
      value: "username",
      sortable: true,
    },
    {
      text: "部署・グループ",
      value: "department",
      sortable: true,
    },
    {
      text: "VPNアカウント",
      value: "vpnAccount",
      sortable: true,
    },
  ];
  headersAdditional: {
    text: string;
    value: string;
    sortable: boolean;
  }[] = [
    { text: "Field1", value: "Field1", sortable: true },
    { text: "Field2", value: "Field2", sortable: true },
    { text: "Field3", value: "Field3", sortable: true },
    { text: "Field4", value: "Field4", sortable: true },
    { text: "Field5", value: "Field5", sortable: true },
    { text: "Field6", value: "Field6", sortable: true },
    { text: "Field7", value: "Field7", sortable: true },
    { text: "Field8", value: "Field8", sortable: true },
    { text: "Field9", value: "Field9", sortable: true },
    { text: "Field10", value: "Field10", sortable: true },
    { text: "Field11", value: "Field11", sortable: true },
    { text: "Field12", value: "Field12", sortable: true },
    { text: "Field13", value: "Field13", sortable: true },
    { text: "Field14", value: "Field14", sortable: true },
    { text: "Field15", value: "Field15", sortable: true },
    { text: "Field16", value: "Field16", sortable: true },
    { text: "Field17", value: "Field17", sortable: true },
    { text: "Field18", value: "Field18", sortable: true },
    { text: "Field19", value: "Field19", sortable: true },
    { text: "Field20", value: "Field20", sortable: true },
  ];
  get headers() {
    return this.headersBasic.concat(this.headersAdditional);
  }

  get defaultBasics() {
    return this.headersBasic.map(() => true);
  }
  get defaultAdditionals() {
    return this.headersAdditional.map(() => false);
  }

  @Mutation
  UpdateHeaderNames(names: string[] | null) {
    for (let i = 0; i < this.headersAdditional.length; i++) {
      this.headersAdditional[i].text = names
        ? names[i]
        : this.headersAdditional[i].value;
    }
  }

  @Mutation
  SetListData(
    payload: assetManagementPb.AssetNodeListResponse.AsObject | null,
  ) {
    this.list.data = payload;
  }

  @Mutation
  SetListLoading(payload: boolean) {
    this.list.loading = payload;
  }

  @Mutation
  SetListError(payload: string | null) {
    this.list.error = payload;
  }

  @Mutation
  ClearListData() {
    this.list.data = null;
  }

  @Action({ rawError: true })
  // eslint-disable-next-line
  async getListAssetNode(payload: { pagination: Pagination }) {
    this.SetListLoading(true);
    await api
      .getListAssetNode({ pagination: { offset: 0, limit: 1000 } })
      .then((res) => {
        this.SetListData(res);
        this.UpdateHeaderNames(
          this.list.data && this.list.data.columns
            ? this.list.data.columns.columnNameListList
            : null,
        );
      })
      .catch((err) => {
        this.ClearListData();
        this.SetListError(getErrorString(err));
        NetFinderCommonModule.AddApiError({
          target: "資産情報一覧取得",
          message: getErrorString(err),
        });
      });
    this.SetListLoading(false);
  }

  // ビュー定義
  get views() {
    if (AuthModule.assetView) {
      return AuthModule.assetView.data.views;
    }
    return [];
  }

  get viewEdittable() {
    return !!AuthModule.assetView;
  }

  @Action({ rawError: true })
  async initViews() {
    // mainの設定を初期化する
    NetFinderCommonModule.InitMainView();
    // 項目名を初期化する
    this.UpdateHeaderNames(null);
  }

  @Action({ rawError: true })
  addView(payload: AssetView) {
    if (AuthModule.assetView) {
      AuthModule.assetView.data.views.push(payload);
      AuthModule.syncAssetView();
    }
  }

  @Action({ rawError: true })
  deleteView(payload: AssetView) {
    if (AuthModule.assetView) {
      const index = AuthModule.assetView.data.views.findIndex(
        (view) => view.name === payload.name,
      );
      if (index >= 0) {
        AuthModule.assetView.data.views.splice(index, 1);
        AuthModule.syncAssetView();
      }
    }
  }

  @Action({ rawError: true })
  async syncView(payload: AssetView) {
    const view = this.views.find((view) => view.name === payload.name);
    if (view) {
      view.shown.basics = payload.shown.basics.slice();
      view.shown.additionals = payload.shown.additionals.slice();
      view.filters = payload.filters.slice();
      AuthModule.syncAssetView();
    }
  }

  // CSVアップロード
  csv: string | null = null;
  csvAssetList: assetManagementPb.Asset[] = [];
  csvAssetColumns: assetManagementPb.AssetColumns | null = null;
  validationErrors: string[] = [];
  uploading: boolean = false;
  uploadError: string | null = null;

  @Mutation
  SetCsv(payload: string | null) {
    this.csv = payload;
    if (this.csv != null) {
      const payload = verifyAssetCSV(this.csv);
      this.csvAssetList = payload.assetList;
      this.csvAssetColumns = payload.columns;
      this.validationErrors = payload.errors;
    } else {
      this.csvAssetList = [];
      this.csvAssetColumns = null;
      this.validationErrors = [];
    }
  }

  @Mutation
  SetUploading(payload: boolean) {
    this.uploading = payload;
  }

  @Mutation
  SetUploadError(payload: string | null) {
    this.uploadError = payload;
  }

  @Action({ rawError: true })
  async uploadListAsset() {
    this.SetUploading(true);
    this.SetUploadError(null);

    if (this.csvAssetColumns != null) {
      await api
        .uploadListAsset({
          assetList: this.csvAssetList,
          columns: this.csvAssetColumns,
        })
        // eslint-disable-next-line
        .then((res) => {})
        .catch((err) => {
          this.SetUploadError(getErrorString(err));
          NetFinderCommonModule.AddApiError({
            target: "資産情報アップロード",
            message: getErrorString(err),
          });
        });
    }
    this.SetUploading(false);
  }
}

export const AssetManagementModule = getModule(AssetManagement);
